import { Application, RequestHandler, ErrorRequestHandler } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';

// Import middleware modules
import * as errorHandler from './error-handler';
import * as validation from './validation';
import * as auth from './auth';
import * as upload from './upload';
import * as correlationId from './correlation-id';
import * as metrics from './metrics';
import * as responseFormatter from './response-formatter';
import * as requestLogger from './request-logger';
import * as rateLimiter from './rate-limiter';
import * as config from './config';
import { v } from '@unidev-hub/validator';

// Import registry functions and types
import {
  registerMiddleware,
  applyRegularMiddleware,
  applyErrorHandlers,
  isMiddlewareEnabled,
  RegularMiddlewareFactory,
  ErrorMiddlewareFactory,
  MiddlewareFactory
} from './registry';
import {MetricsOptions} from "./types";

// Re-export registry types
export type {
  RegularMiddlewareFactory,
  ErrorMiddlewareFactory,
  MiddlewareFactory
} from './registry';

// Re-export registry functions
export {
  registerMiddleware,
  applyRegularMiddleware,
  applyErrorHandlers,
  isMiddlewareEnabled
} from './registry';

// Re-export middleware functions
export {
  HttpError,
  errorHandlerMiddleware,
  asyncHandler,
  applyErrorHandler
} from './error-handler';

export {
  validate,
  validateAll,
  validateQuery,
  validateRequestAndQuery
} from './validation';

export {
  setupAuth,
  authenticate,
  authorizeRoles,
  authorizePermissions,
  authorizeOwnership
} from './auth';

export {
  upload
} from './upload';

export {
  correlationIdMiddleware
} from './correlation-id';

export {
  metricsMiddleware,
  metrics
} from './metrics';

export {
  responseFormatterMiddleware
} from './response-formatter';

export {
  requestLoggerMiddleware
} from './request-logger';

export {
  rateLimiterMiddleware
} from './rate-limiter';

// Re-export configuration utilities
export {
  configMiddleware,
  initializeMiddleware,
  getConfig
} from './config';

// Re-export validator
export { v } from '@unidev-hub/validator';

/**
 * Initialize and apply all middleware to an Express application
 * For backward compatibility with existing code
 *
 * @param app Express application
 * @param configManager
 * @param logger Logger instance
 */
export async function applyMiddleware(
  app: Application,
  configManager: ConfigManager,
  logger: Logger
): Promise<Application> {
  // Check if we should use the new configurable middleware system
  const useConfigurableMiddleware = configManager.get('api.useConfigurableMiddleware', false);

  if (useConfigurableMiddleware) {
    logger.info('Using configurable middleware system');

    // Apply regular middleware
    applyRegularMiddleware(app, configManager, logger);

    // Handle special cases

    // Setup authentication if enabled
    if (isMiddlewareEnabled('auth', configManager)) {
      try {
        app.locals.authMiddleware = await auth.setupAuth(
          app,
          configManager,
          logger,
          configManager.get('api.auth', {})
        );
        logger.debug('Authentication middleware initialized');
      } catch (error) {
        logger.error('Failed to initialize auth middleware:', error as Error);
        const authRequired = configManager.get('api.auth.required', false);
        if (authRequired) {
          throw error;
        }
      }
    }

    // Handle metrics endpoint separately if enabled
    if (isMiddlewareEnabled('metrics', configManager)) {
      try {
        const metricsConfig = configManager.get<MetricsOptions>('api.metrics', {});
        const endpointPath = metricsConfig.endpoint || '/metrics';

        // Define the expected type for metrics middleware result
        interface MetricsMiddlewareResult {
          middleware: RequestHandler;
          endpoint: RequestHandler;
        }

        // Call the metrics middleware function with explicit typing
        const metricsResult = metrics.metricsMiddleware(metricsConfig) as MetricsMiddlewareResult;

        // Apply the metrics middleware
        if (metricsResult && typeof metricsResult.middleware === 'function') {
          app.use(metricsResult.middleware);
          logger.debug('Metrics middleware applied');
        }

        // Register metrics endpoint
        if (metricsResult && typeof metricsResult.endpoint === 'function') {
          app.get(endpointPath, metricsResult.endpoint);
          logger.debug(`Metrics endpoint registered at: ${endpointPath}`);
        } else {
          logger.warn('Metrics middleware did not return expected endpoint handler');
        }
      } catch (error) {
        logger.error('Failed to initialize metrics endpoint:', error as Error);
      }
    }

    // Apply error handlers at the end
    applyErrorHandlers(app, configManager, logger);

    return app;
  }

  // Legacy implementation for backward compatibility
  // Initialize middleware with config
  config.initializeMiddleware(configManager, logger);

  // Get middleware configuration
  const middlewareConfig = config.getConfig();

  // Apply correlation ID middleware
  app.use(correlationId.correlationIdMiddleware(middlewareConfig.correlation));

  // Apply request logging middleware
  app.use(requestLogger.requestLoggerMiddleware(logger, middlewareConfig.logging));

  // Apply response formatting middleware
  app.use(responseFormatter.responseFormatterMiddleware(middlewareConfig.responses));

  // Apply rate limiting if configured
  if (middlewareConfig.rateLimiting && (middlewareConfig?.rateLimiting?.max ?? 0) > 0) {
    app.use(rateLimiter.rateLimiterMiddleware(middlewareConfig.rateLimiting));
  }

  // Apply metrics middleware
  if (middlewareConfig.metrics) {
    try {
      const metricsResult = metrics.metricsMiddleware(middlewareConfig.metrics);

      // Type guard to ensure it has the expected properties
      if (metricsResult && typeof metricsResult === 'object') {
        // Apply middleware if it exists
        if ('middleware' in metricsResult && typeof metricsResult.middleware === 'function') {
          app.use(metricsResult.middleware);
        }

        // Register metrics endpoint if it exists
        if ('endpoint' in metricsResult && typeof metricsResult.endpoint === 'function') {
          const metricsEndpoint = 
            middlewareConfig.metrics && typeof middlewareConfig.metrics === 'object' && 
            'endpoint' in middlewareConfig.metrics ? 
            String(middlewareConfig.metrics.endpoint) : 
            '/metrics';
          
          app.get(metricsEndpoint, metricsResult.endpoint);
          logger.debug(`Metrics endpoint registered at: ${metricsEndpoint}`);
        }
      } else {
        logger.warn('Metrics middleware did not return expected properties');
      }
    } catch (error) {
      logger.error('Failed to initialize metrics middleware:', error as Error);
    }
  }

  // Setup authentication
  // Store auth middleware in app.locals for convenience
  try {
    app.locals.authMiddleware = await auth.setupAuth(app, configManager, logger, middlewareConfig.auth);
  } catch (error) {
    logger.error('Failed to initialize auth middleware:', error as Error);
    const authRequired = middlewareConfig.auth?.required === true;
    if (authRequired) {
      throw error;
    }
  }

  // Apply error handler middleware at the end
  app.use(errorHandler.errorHandlerMiddleware(middlewareConfig.errors, logger));

  return app;
}

// Create a unified middleware object for convenience
export const middleware = {
  applyMiddleware,
  applyRegularMiddleware,
  applyErrorHandlers,
  registerMiddleware,
  isMiddlewareEnabled,
  validate: validation.validate,
  validateAll: validation.validateAll,
  validateQuery: validation.validateQuery,
  validateRequestAndQuery: validation.validateRequestAndQuery,
  authenticate: auth.authenticate,
  authorizeRoles: auth.authorizeRoles,
  authorizePermissions: auth.authorizePermissions,
  authorizeOwnership: auth.authorizeOwnership,
  upload: upload.upload,
  asyncHandler: errorHandler.asyncHandler,
  HttpError: errorHandler.HttpError
};

export default middleware;

// Import and re-export Middleware enum and MiddlewareGroups from types
import { Middleware, MiddlewareGroups } from './types';
export { Middleware, MiddlewareGroups };

// Also export types from types.ts
export type {
  MetricsOptions,
  CorrelationIdOptions,
  ErrorHandlingOptions,
  ResponseFormatOptions,
  RateLimitOptions,
  RequestLoggerOptions,
  ValidationOptions,
  UploadOptions,
  AuthOptions,
  // Add any other types you need to export
} from './types';
